import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CatMascot, LogoFull, Tape, Topo } from "@/components/Brand";
import { STATUS, fmtClock } from "@/lib/format";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Logo = ({ className = "h-10" }) => <LogoFull className={className} />;

export const Brand = ({ sub, className = "h-14" }) => (
  <div className="flex flex-col items-start" data-testid="brand">
    <LogoFull className={className} />
    {sub && <Tape className="-mt-1 ml-2">{sub}</Tape>}
  </div>
);

export const FullLoader = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-3" data-testid="full-loader">
    <CatMascot jump className="w-32" />
    <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3323]"><Loader2 className="h-4 w-4 animate-spin" /> Pemanasan dulu...</div>
  </div>
);

export const StatusBadge = ({ status, overdue, className, testid }) => {
  const s = overdue ? STATUS.OVERDUE : STATUS[status] || { label: status, cls: "" };
  return (
    <span
      data-testid={testid || "status-badge"}
      className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide", s.cls, className)}
    >
      {s.label}
    </span>
  );
};

export const Pill = ({ className, children, testid }) => (
  <span data-testid={testid} className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", className)}>
    {children}
  </span>
);

export const Empty = ({ title, desc, children, testid = "empty-state" }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-[22px] border border-dashed border-[#1A3323]/20 bg-white/60 px-5 py-8 text-center">
    <Topo className="absolute -right-16 -top-10 h-48 w-72 text-[#3D6F4B]/10" />
    <CatMascot jump className="relative mx-auto mb-3 w-24" />
    <div className="font-bold text-[#1A3323]">{title}</div>
    {desc && <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{desc}</p>}
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export function useNow(offset = 0, ms = 1000) {
  const [t, setT] = useState(Date.now() + offset);
  useEffect(() => {
    const id = setInterval(() => setT(Date.now() + offset), ms);
    return () => clearInterval(id);
  }, [offset, ms]);
  return t;
}

export function Timer({ startedAt, rotationMinutes = 5, offset = 0, big, huge }) {
  const t = useNow(offset);
  const elapsed = Math.max(0, (t - Date.parse(startedAt)) / 1000);
  const over = elapsed >= rotationMinutes * 60;
  if (huge) {
    return (
      <div data-testid="climb-timer" className={cn("font-mono text-7xl font-extrabold leading-none tabular sm:text-8xl", over ? "text-red-600" : "text-[#1A3323]")}>{fmtClock(elapsed)}</div>
    );
  }
  return (
    <div data-testid="climb-timer" className={cn("font-mono font-extrabold tabular", big ? "text-5xl" : "text-lg", over ? "text-red-600" : "text-[#1A3323]")}>
      {fmtClock(elapsed)} <span className="text-base font-semibold text-muted-foreground">/ {fmtClock(rotationMinutes * 60)}</span>
    </div>
  );
}

export const isOverdue = (tk, offset = 0) =>
  tk?.status === "CLIMBING" && (tk.overdue || (tk.expected_end_at && Date.now() + offset >= Date.parse(tk.expected_end_at)));

export function Confirm({ trigger, title, desc, confirmText = "Ya, lanjutkan", onConfirm, destructive, testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testid}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {desc && <AlertDialogDescription>{desc}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>Batal</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testid}-confirm`}
            onClick={onConfirm}
            className={destructive ? "bg-red-600 hover:bg-red-700" : ""}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
